import type {GridPoint, LevelGeometryDocument} from "@/dungeons/model/DungeonGeometry.ts";
import {gridToWorld, PIXELS_PER_METER} from "@/dungeons/geometry/Coordinates.ts";
import type {DoorState} from "@/encounter/doors/DoorState.ts";
import type Combatant from "@/encounter/model/Combatant.ts";
import {DEFAULT_SIGHT_RADIUS_METERS} from "./SightRange.ts";

export type Point = {x: number; y: number};
export type Segment = {start: Point; end: Point};
export type VisibilityPolygon = Point[];

export const SIGHT_RADIUS_WORLD = DEFAULT_SIGHT_RADIUS_METERS * PIXELS_PER_METER;

const EPSILON = 1e-7;
const ANGLE_EPSILON = 1e-5;
const CIRCLE_SAMPLES = 192;

export function sightBlockers(
    geometry: LevelGeometryDocument,
    doorState: (door: LevelGeometryDocument["doors"][number]) => DoorState,
): Segment[] {
    const toWorld = (point: GridPoint) => gridToWorld(point, geometry.grid);
    const walls = geometry.walls.flatMap(wall => wall.points.slice(1).map((point, index) => ({
        start: toWorld(wall.points[index]),
        end: toWorld(point),
    })));
    const closedDoors = geometry.doors
        .filter(door => doorState(door) === "CLOSED")
        .map(door => ({start: toWorld(door.start), end: toWorld(door.end)}));
    return [...walls, ...closedDoors].filter(segment =>
        segment.start.x !== segment.end.x || segment.start.y !== segment.end.y,
    );
}

const cross = (a: Point, b: Point) => a.x * b.y - a.y * b.x;
const subtract = (a: Point, b: Point): Point => ({x: a.x - b.x, y: a.y - b.y});

function pointOnSegment(point: Point, segment: Segment): boolean {
    const direction = subtract(segment.end, segment.start);
    const offset = subtract(point, segment.start);
    const lengthSquared = direction.x * direction.x + direction.y * direction.y;
    if (lengthSquared < EPSILON * EPSILON) return Math.hypot(offset.x, offset.y) <= EPSILON;
    return Math.abs(cross(direction, offset)) <= EPSILON * Math.sqrt(lengthSquared)
        && offset.x * direction.x + offset.y * direction.y >= -EPSILON
        && offset.x * direction.x + offset.y * direction.y <= lengthSquared + EPSILON;
}

export function visionSources(combatants: readonly Combatant[], levelId: string, side: "friendly" | "hostile") {
    return combatants.filter(combatant => combatant.levelId === levelId
        && combatant.hostile === (side === "hostile")
        && (side === "hostile" || !combatant.hidden));
}

function circleIntersections(origin: Point, radius: number, segment: Segment): Point[] {
    const edge = subtract(segment.end, segment.start);
    const offset = subtract(segment.start, origin);
    const a = edge.x * edge.x + edge.y * edge.y;
    const b = 2 * (offset.x * edge.x + offset.y * edge.y);
    const c = offset.x * offset.x + offset.y * offset.y - radius * radius;
    const discriminant = b * b - 4 * a * c;
    if (a < EPSILON || discriminant < 0) return [];
    return [(-b - Math.sqrt(discriminant)) / (2 * a), (-b + Math.sqrt(discriminant)) / (2 * a)]
        .filter(position => position >= 0 && position <= 1)
        .map(position => ({x: segment.start.x + position * edge.x, y: segment.start.y + position * edge.y}));
}

function segmentWithinRadius(origin: Point, radius: number, segment: Segment): boolean {
    const edge = subtract(segment.end, segment.start);
    const lengthSquared = edge.x * edge.x + edge.y * edge.y;
    const offset = subtract(origin, segment.start);
    if (lengthSquared < EPSILON * EPSILON) return Math.hypot(offset.x, offset.y) <= radius;
    const position = Math.max(0, Math.min(1,
        (offset.x * edge.x + offset.y * edge.y) / lengthSquared,
    ));
    return Math.hypot(offset.x - edge.x * position, offset.y - edge.y * position) <= radius;
}

function rayDistance(origin: Point, direction: Point, segment: Segment): number | undefined {
    const edge = subtract(segment.end, segment.start);
    const denominator = cross(direction, edge);
    const offset = subtract(segment.start, origin);
    if (Math.abs(denominator) < EPSILON) {
        if (Math.abs(cross(offset, direction)) >= EPSILON) return undefined;
        const start = offset.x * direction.x + offset.y * direction.y;
        const end = start + edge.x * direction.x + edge.y * direction.y;
        return Math.max(start, end) < 0 ? undefined : Math.max(0, Math.min(start, end));
    }
    const distance = cross(offset, edge) / denominator;
    const position = cross(offset, direction) / denominator;
    return distance >= 0 && position >= -EPSILON && position <= 1 + EPSILON
        ? distance : undefined;
}

export function visibilityPolygon(
    origin: Point,
    segments: readonly Segment[],
    radius = SIGHT_RADIUS_WORLD,
): VisibilityPolygon {
    if (!(radius > 0) || !Number.isFinite(origin.x) || !Number.isFinite(origin.y)) return [];
    // An eye exactly on a blocker has no well-defined side. Keep it dark until moved off the wall.
    if (segments.some(segment => pointOnSegment(origin, segment))) return [];

    const nearbySegments = segments.filter(segment => segmentWithinRadius(origin, radius, segment));

    const angles = Array.from({length: CIRCLE_SAMPLES}, (_, index) => index * 2 * Math.PI / CIRCLE_SAMPLES);
    nearbySegments.forEach(segment => {
        const intersections = circleIntersections(origin, radius, segment);
        for (const point of [segment.start, segment.end, ...intersections]) {
            if (Math.hypot(point.x - origin.x, point.y - origin.y) > radius + EPSILON) continue;
            const angle = Math.atan2(point.y - origin.y, point.x - origin.x);
            for (const candidate of [angle - ANGLE_EPSILON, angle, angle + ANGLE_EPSILON]) {
                angles.push((candidate + 2 * Math.PI) % (2 * Math.PI));
            }
        }
    });

    return angles.map(angle => {
        const direction = {x: Math.cos(angle), y: Math.sin(angle)};
        let distance = radius;
        for (const segment of nearbySegments) {
            const hit = rayDistance(origin, direction, segment);
            if (hit !== undefined && hit < distance) distance = hit;
        }
        return {
            angle,
            point: {x: origin.x + direction.x * distance, y: origin.y + direction.y * distance},
        };
    }).sort((a, b) => a.angle - b.angle).map(hit => hit.point);
}

export function hasLineOfSight(origin: Point, target: Point, segments: readonly Segment[]): boolean {
    const dx = target.x - origin.x;
    const dy = target.y - origin.y;
    const distance = Math.hypot(dx, dy);
    if (distance <= EPSILON) return true;
    const direction = {x: dx / distance, y: dy / distance};
    const minX = Math.min(origin.x, target.x);
    const maxX = Math.max(origin.x, target.x);
    const minY = Math.min(origin.y, target.y);
    const maxY = Math.max(origin.y, target.y);
    return !segments.some(segment => {
        if (Math.max(segment.start.x, segment.end.x) < minX
            || Math.min(segment.start.x, segment.end.x) > maxX
            || Math.max(segment.start.y, segment.end.y) < minY
            || Math.min(segment.start.y, segment.end.y) > maxY) return false;
        const hit = rayDistance(origin, direction, segment);
        return hit !== undefined && hit < distance - EPSILON;
    });
}

export function pointInVisibility(point: Point, polygons: readonly VisibilityPolygon[]): boolean {
    return polygons.some(polygon => {
        if (polygon.length < 3) return false;
        let inside = false;
        for (let index = 0, previous = polygon.length - 1; index < polygon.length; previous = index++) {
            const start = polygon[previous];
            const end = polygon[index];
            if (pointOnSegment(point, {start, end})) return true;
            if ((start.y > point.y) !== (end.y > point.y)
                && point.x < (end.x - start.x) * (point.y - start.y) / (end.y - start.y) + start.x) {
                inside = !inside;
            }
        }
        return inside;
    });
}
